// Canonical, shareable web URLs for artists / tracks / mixes — plus a
// cross-platform dispatcher to share them.
//
// The URLs MUST match the mixgoat-website slug scheme byte-for-byte, or the
// shared link 404s. The website builds slugs as
//     encodeURIComponent(value.toLowerCase().replace(/\s+/g, '-'))
// (see mixgoat-website src/app/{track,mix,artist}/.../page.tsx and
// lib/api.ts buildMixSlug) and reverses them on the page with
// decodeURIComponent(...).replace(/-/g, ' '). Keep this in lockstep.
//
// We deliberately share an https://mixgoat.com link (NOT a mixgoat:// scheme):
// an https link previews nicely in messengers and — once Universal Links
// (iOS) / App Links (Android) are live (Phase 2) — opens the native app when
// installed, falling back to the website otherwise.

import { isNative } from './webOnly';

const SHARE_BASE = 'https://mixgoat.com';

function slug(value) {
    return encodeURIComponent(String(value || '').trim().toLowerCase().replace(/\s+/g, '-'));
}

export function buildArtistShareUrl(name) {
    const n = (name || '').trim();
    return n ? `${SHARE_BASE}/artist/${slug(n)}` : null;
}

export function buildTrackShareUrl(artist, title) {
    const a = (artist || '').trim();
    const t = (title || '').trim();
    return a && t ? `${SHARE_BASE}/track/${slug(a)}/${slug(t)}` : null;
}

export function buildMixShareUrl(artist, title) {
    const a = (artist || '').trim();
    const t = (title || '').trim();
    if (!a && !t) return null;
    return `${SHARE_BASE}/mix/${slug(a)}/${slug(t)}`;
}

// Share a URL. Mobile (Capacitor) → native share sheet; desktop/web →
// clipboard. Returns one of: 'shared' | 'copied' | 'cancelled' | 'failed'.
// The native branch reads the plugin off the Capacitor global rather than a
// static import so the desktop/web webpack bundle never pulls in @capacitor/share
// (the plugin is registered there only after `npx cap sync` on a native build).
export async function shareLink(url, title) {
    if (!url) return 'failed';

    // Compute "native" LIVE off the Capacitor global — exactly like
    // generateShareImage's shareImage() does — NOT the module-load-time
    // `isNative` constant. That constant is evaluated once when the bundle first
    // runs; if the Capacitor bridge isn't ready yet (seen on iOS) it's stuck
    // false for the session, so the native share sheet is skipped and we silently
    // fall to clipboard. (Share IMAGE worked because it already checks live;
    // Share PAGE didn't — that's the difference.)
    const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
    const isNativeLive = !!(cap && cap.isNativePlatform && cap.isNativePlatform());
    const Share = cap && cap.Plugins && cap.Plugins.Share;

    if (isNativeLive && Share) {
        try {
            await Share.share({ title: title || 'MixGoat', url });
            return 'shared';
        } catch (err) {
            // User dismissed the sheet — respect it, don't fall back to clipboard.
            if (err && /cancel/i.test(err.message || '')) return 'cancelled';
            return 'failed';   // surface a real failure rather than silently copying
        }
    }

    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
            return 'copied';
        }
    } catch (_) { /* fall through to legacy path */ }

    try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        return 'copied';
    } catch (_) {
        return 'failed';
    }
}

// COPY a URL to the clipboard on every platform. Returns 'copied' | 'failed'.
//
// On native (Capacitor) the WEB clipboard is unreliable: Android's WebView
// denies clipboard-write on the non-secure http://localhost origin, and iOS
// WKWebView's document.execCommand('copy') almost always returns false
// (measured ≈2/20 success on iOS, 0/10 on Android). So on native we go through
// the native @capacitor/clipboard plugin, read off the Capacitor global at
// runtime so the desktop/web webpack bundle never pulls it in. Desktop/web keep
// the navigator.clipboard → execCommand web path below.
export async function copyLink(url) {
    if (!url) return 'failed';

    if (isNative && typeof window !== 'undefined') {
        const Clipboard = window.Capacitor?.Plugins?.Clipboard;
        if (Clipboard) {
            try {
                await Clipboard.write({ string: url });
                return 'copied';
            } catch (_) { /* fall through to the web path */ }
        }
    }

    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
            return 'copied';
        }
    } catch (_) { /* fall through to the legacy path (older WebViews) */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.readOnly = true;
        ta.contentEditable = 'true';            // iOS WKWebView needs this for execCommand('copy')
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        const range = document.createRange();
        range.selectNodeContents(ta);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        ta.setSelectionRange(0, url.length);
        const ok = document.execCommand('copy');
        ta.remove();
        return ok ? 'copied' : 'failed';
    } catch (_) {
        return 'failed';
    }
}
